// RiskDetailPage — T2 (Object Detail + Tabs) layout.
// Displays a single risk with tabbed sections.
// Route: /t/:slug/risks/:riskId (and sub-routes for tabs)

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { risksApi, lifecycleStateLabels, lifecycleStateColors } from '@/lib/api/risks'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { DetailPageShell, type TabDef } from '@/components/shared/DetailPageShell'
import { Button } from '@/components/ui/button'
import { useToast } from '@/lib/hooks/use-toast'
import { MoveRiskDialog } from './MoveRiskDialog'
import { User, Calendar, Tag, CornerLeftUp, MoveRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'

const tabs: TabDef[] = [
  { id: 'detail', label: 'Detail', path: '' },
  { id: 'controls', label: 'Controls', path: '/controls', countKey: 'linked_control_count' },
  { id: 'actions', label: 'Actions', path: '/actions', countKey: 'linked_action_count' },
  { id: 'events', label: 'Events', path: '/events', countKey: 'linked_event_count' },
  { id: 'children', label: 'Children', path: '/children', countKey: 'child_risk_count' },
  { id: 'history', label: 'Audit Log', path: '/history' },
]

export function RiskDetailPage() {
  const { slug } = useTenant()
  const { riskId } = useParams({ strict: false }) as { riskId: string }
  const api = useApi()
  const riskApi = risksApi(api)
  const [moveOpen, setMoveOpen] = useState(false)
  const { toast } = useToast()

  const {
    data: risk,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.risks.detail(slug, riskId),
    queryFn: () => riskApi.get(riskId),
  })

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load risk"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!risk) return null

  return (
    <DetailPageShell
      title={risk.name}
      code={risk.short_code || undefined}
      stateLabel={lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
      stateColorClass={lifecycleStateColors[risk.lifecycle_state]}
      backUrl={`/t/${slug}/risks`}
      backLabel="Back to risk list"
      tabs={tabs}
      basePath={`/t/${slug}/risks/${riskId}`}
      data={risk as unknown as Record<string, unknown>}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => setMoveOpen(true)}>
            <MoveRight className="mr-1.5 h-3.5 w-3.5" />
            Move to...
          </Button>
          <MoveRiskDialog
            open={moveOpen}
            onClose={() => setMoveOpen(false)}
            riskId={riskId}
            currentRegisterId={risk.register_id}
            onMoved={(parentSevered) => {
              setMoveOpen(false)
              if (parentSevered) {
                toast({
                  title: 'Parent link removed',
                  description:
                    "This risk was moved outside its parent risk's register. The parent-child link has been removed.",
                })
              }
            }}
          />
        </>
      }
      metadata={
        <>
          {risk.parent_risk_id && risk.parent_risk_name && (
            <Link
              to="/t/$slug/risks/$riskId"
              params={{ slug, riskId: risk.parent_risk_id }}
              className="flex items-center gap-1 text-primary hover:underline"
            >
              <CornerLeftUp className="h-3.5 w-3.5" />
              Child of: {risk.parent_risk_name}
            </Link>
          )}
          <span className="flex items-center gap-1">
            <Tag className="h-3.5 w-3.5" />
            {risk.taxonomy_node_name}
          </span>
          {risk.owner_display_name && (
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {risk.owner_display_name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(risk.updated_at).toLocaleDateString()}
          </span>
        </>
      }
    />
  )
}
